<template>
  <v-app>
    <v-app-bar 
      v-if="!isLoginPage"
      app 
      :color="$vuetify.theme.dark ? 'surface' : 'primary'" 
      :dark="!$vuetify.theme.dark"
      elevation="4"
      class="modern-app-bar"
    >
      <v-toolbar-title class="d-flex align-center">
        <img 
          src="/favicon.ico" 
          alt="Logo do Projeto" 
          class="mr-2" 
          style="width: 32px; height: 32px;"
        />
        <span class="text-h6 font-weight-bold">Sistema de Atualização Automática</span>
        <!-- Usuários - Apenas Admin (gestor não vê) -->
        <v-btn
          v-if="isAuthenticated && isAdmin"
          text
          :to="{ name: 'Usuarios' }"
          :active-class="''"
          :class="{ 'v-btn--active': $route.name === 'Usuarios' }"
          class="nav-btn ml-4"
        >
          <v-icon left>mdi-account-group</v-icon>
          Usuários
        </v-btn>
        
        <!-- Configurações de Score - Apenas Admin -->
        <v-btn
          v-if="isAuthenticated && isAdmin"
          text
          :to="{ name: 'ConfiguracaoScore' }"
          :active-class="''"
          :class="{ 'v-btn--active': $route.name === 'ConfiguracaoScore' }"
          class="nav-btn ml-2"
        >
          <v-icon left>mdi-cog</v-icon>
          Configurações
        </v-btn>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      
      <!-- Menu de navegação -->
      <template v-if="isAuthenticated">
        
        <!-- Hosts - Admin e Gestor -->
        <v-btn
          v-if="isAdminOrGestor"
          text
          :to="{ name: 'Home' }"
          :active-class="''"
          :class="{ 'v-btn--active': $route.name === 'Home' }"
          class="nav-btn"
        >
          <v-icon left>mdi-home</v-icon>
          Hosts
        </v-btn>
        
        <!-- Cronogramas - Admin e Gestor -->
        <v-btn
          v-if="isAdminOrGestor"
          text
          :to="{ name: 'Cronogramas' }"
          :active-class="''"
          :class="{ 'v-btn--active': $route.name === 'Cronogramas' }"
          class="nav-btn"
        >
          <v-icon left>mdi-calendar-clock</v-icon>
          Cronogramas
        </v-btn>
        
        <!-- Dashboard - Todos os usuários -->
        <v-btn
          text
          :to="{ name: 'Dashboard' }"
          :active-class="''"
          :class="{ 'v-btn--active': $route.name === 'Dashboard' }"
          class="nav-btn"
        >
          <v-icon left>mdi-view-dashboard</v-icon>
          Dashboard
        </v-btn>
        
        <!-- Toggle de tema -->
        <v-btn
          icon
          @click="toggleTheme"
          class="ml-2 theme-toggle"
        >
          <v-icon>{{ $vuetify.theme.dark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
        </v-btn>
        
        <!-- Informações do usuário -->
        <v-chip
          small
          :color="getUserChipColor()"
          class="ml-3 user-chip"
          outlined
        >
          <v-icon left small>{{ getUserChipIcon() }}</v-icon>
          {{ currentUser?.username || 'Usuário' }} ({{ translateRole(currentUser?.role) || 'N/A' }})
        </v-chip>
      </template>
      
      <!-- Indicador de status da conexão WebSocket -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            small
            :color="wsConnected ? 'light-green' : 'error'"
            class="mr-3 connection-chip"
            v-bind="attrs"
            v-on="on"
            @click="reconnectWebSocket"
            outlined
          >
            <v-icon left small>{{ wsConnected ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            {{ wsConnected ? 'Conectado' : 'Desconectado' }}
          </v-chip>
        </template>
        <span>
          {{ wsConnected 
            ? 'Conectado ao serviço de notificações em tempo real' 
            : 'Clique para tentar reconectar ao servidor' }}
        </span>
      </v-tooltip>
      <v-btn 
        v-if="isAuthenticated" 
        :color="$vuetify.theme.dark ? 'error' : 'white'" 
        :text="!$vuetify.theme.dark"
        class="ml-2 logout-btn" 
        @click="handleLogout"
      >
        <v-icon left>mdi-logout</v-icon>
        Sair
      </v-btn>
    </v-app-bar>

    <v-main>
      <v-container fluid>
        <router-view />
      </v-container>
    </v-main>

    <!-- Snackbar para notificações em tempo real -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      bottom
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>

    <v-footer v-if="!isLoginPage" app>
      <span>&copy; {{ new Date().getFullYear() }}</span>
    </v-footer>
  </v-app>
</template>

<script>
import websocketService from '@/services/websocketService';
import authService from '@/services/authService';
import themeService from '@/services/themeService';

export default {
  name: 'App',
  data: () => ({
    wsConnected: false,
    snackbar: {
      show: false,
      text: '',
      color: 'info',
      timeout: 5000
    },
    // Propriedade reativa para forçar atualização do estado de autenticação
    authCheckTrigger: 0,
    lastAuthState: authService.isAuthenticated() // Estado anterior da autenticação
  }),
  
  created() {
    // Inicializar tema
    themeService.initializeTheme();
    
    // Inicia a conexão WebSocket quando o componente é criado
    this.initWebSocket();
    
    // Adicionar listener para mudanças no localStorage
    window.addEventListener('storage', this.handleStorageChange);
    
    // Listener para evento personalizado de mudança de autenticação
    window.addEventListener('auth-state-changed', this.handleAuthStateChanged);
    
    // Listener para mudanças no localStorage dentro da mesma aba
    this.checkAuthInterval = setInterval(() => {
      const currentAuth = authService.isAuthenticated();
      if (this.lastAuthState !== currentAuth) {
        this.lastAuthState = currentAuth;
        this.authCheckTrigger++;
      }
    }, 1000);
    
    // Listener para mudanças de rota para forçar atualização da interface
    this.$router.beforeEach((to, from, next) => {
      // Forçar atualização da interface antes da mudança de rota
      this.$nextTick(() => {
        this.forceInterfaceUpdate();
      });
      next();
    });
    
    // Registrar método global para forçar atualização da autenticação
    this.$root.$on('force-auth-update', this.forceAuthUpdate);
    
    // Registrar método global para forçar atualização da interface
    this.$root.$on('force-interface-update', this.forceInterfaceUpdate);
  },
  
  beforeDestroy() {
    // Garante que a conexão WebSocket seja encerrada quando o componente for destruído
    websocketService.disconnect();
    
    // Remove listener do localStorage
    window.removeEventListener('storage', this.handleStorageChange);
    
    // Remove listener do evento personalizado
    window.removeEventListener('auth-state-changed', this.handleAuthStateChanged);
    
    // Limpar interval
    if (this.checkAuthInterval) {
      clearInterval(this.checkAuthInterval);
    }
    
    // Remover listener global
    this.$root.$off('force-auth-update', this.forceAuthUpdate);
  },
  
  computed: {
    isLoginPage() {
      return this.$route.name === 'Login' || this.$route.path === '/login';
    },
    
    isAuthenticated() {
      // Usar a propriedade reativa para forçar recálculo
      this.authCheckTrigger;
      
      return authService.isAuthenticated();
    },
    
    isAdmin() {
      // Usar a propriedade reativa para forçar recálculo
      this.authCheckTrigger;
      
      return authService.isAdmin();
    },
    
    isGestor() {
      // Usar a propriedade reativa para forçar recálculo
      this.authCheckTrigger;
      
      return authService.isGestor();
    },
    
    isAdminOrGestor() {
      // Usar a propriedade reativa para forçar recálculo
      this.authCheckTrigger;
      
      return authService.isAdminOrGestor();
    },
    
    isUser() {
      // Usar a propriedade reativa para forçar recálculo
      this.authCheckTrigger;
      
      return authService.isUser();
    },
    
    currentUser() {
      // Usar a propriedade reativa para forçar recálculo
      this.authCheckTrigger;
      
      return authService.getUser();
    }
  },

  watch: {
    // Watchers para forçar atualização em mudanças de rota
    $route() {
      this.authCheckTrigger++;
      
      // Forçar re-renderização da interface após mudança de rota
      this.$nextTick(() => {
        this.forceInterfaceUpdate();
      });
    },
    
    // Watcher para sincronizar mudanças de tema
    '$vuetify.theme.dark': {
      handler(newValue) {
        console.log('🎨 Tema do Vuetify mudou:', newValue ? 'dark' : 'light')
        // Aplicar classes CSS correspondentes
        if (document.body) {
          document.body.classList.remove('theme-light', 'theme-dark')
          document.body.classList.add(newValue ? 'theme-dark' : 'theme-light')
        }
        
        // Atualizar atributo data-theme
        if (document.documentElement) {
          document.documentElement.setAttribute('data-theme', newValue ? 'dark' : 'light')
        }
      },
      immediate: true
    }
  },
  
  methods: {
    /**
     * Inicializa a conexão WebSocket e configura os handlers
     */
    initWebSocket() {
      // Registra handler para receber mensagens do WebSocket
      websocketService.onMessage(this.handleWsMessage);
      
      // Inicia a conexão
      websocketService.connect();
    },
    
    /**
     * Processa mensagens recebidas pelo WebSocket
     * @param {Object} data - Dados da mensagem
     */
    handleWsMessage(data) {
      // Atualiza o status de conexão quando recebe eventos de conexão
      if (data.type === 'connection') {
        this.wsConnected = data.status === 'connected';
        return;
      }
      
      // Processa diferentes tipos de notificações
      switch (data.type) {
        case 'info':
          this.showNotification(data.message, 'info');
          break;
          
        case 'progresso':
          this.showNotification(
            `Atualização de ${data.sistema} em ${data.hostId}: ${data.progresso}% (${data.status})`, 
            'info'
          );
          break;
          
        case 'conclusao':
          this.showNotification(
            `Atualização de ${data.sistema} em ${data.hostId} ${data.sucesso ? 'concluída com sucesso' : 'falhou'}: ${data.mensagem}`,
            data.sucesso ? 'success' : 'warning'
          );
          
          // Se a atualização foi bem-sucedida, emitir evento para atualizar o frontend
          if (data.sucesso) {
            // Emitir evento global para que o HomeView possa atualizar as versões
            this.$root.$emit('atualizacao-concluida', {
              hostId: data.hostId,
              sistema: data.sistema,
              mensagem: data.mensagem
            });
          }
          break;
          
        case 'sincronizacao_concluida':
          this.showNotification(
            data.message,
            data.success ? 'success' : 'error'
          );
          
          // Se a sincronização foi bem-sucedida, emitir evento para atualizar o frontend
          if (data.success) {
            this.$root.$emit('sincronizacao-concluida', {
              newHostsCount: data.newHostsCount || 0,
              totalHostsCount: data.totalHostsCount || 0,
              message: data.message
            });
          }
          break;
          
        case 'erro':
          this.showNotification(
            `Erro na atualização de ${data.sistema} em ${data.hostId}: ${data.erro}`,
            'error'
          );
          break;
          
        case 'atualizacao_versao':
          this.showNotification(
            `Versão do ${data.sistema} atualizada para ${data.novaVersao} em ${data.hostName}`,
            'success'
          );
          
          // Emitir evento global para atualizar as versões no frontend
          this.$root.$emit('versao-atualizada', {
            hostId: data.hostId,
            sistema: data.sistema,
            novaVersao: data.novaVersao,
            hostName: data.hostName
          });
          break;
      }
    },
    
    /**
     * Exibe uma notificação usando o snackbar
     * @param {string} text - Texto da notificação
     * @param {string} color - Cor da notificação (info, success, warning, error)
     * @param {number} timeout - Tempo de exibição em ms
     */
    showNotification(text, color = 'info', timeout = 5000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout
      };
    },

    /**
     * Tenta reconectar ao servidor WebSocket
     */
    reconnectWebSocket() {
      // Mostra notificação para o usuário
      this.showNotification('Tentando reconectar ao servidor...', 'info');
      
      // Garante que a conexão WebSocket seja encerrada antes de tentar reconectar
      websocketService.disconnect();
      
      // Pequeno timeout para garantir que a conexão seja encerrada
      setTimeout(() => {
        // Inicia a conexão WebSocket
        this.initWebSocket();
        
        // Verifica após 3 segundos se a conexão foi estabelecida
        setTimeout(() => {
          if (!this.wsConnected) {
            this.showNotification('Falha ao reconectar. Tentando novamente em alguns segundos...', 'error');
          }
        }, 3000);
      }, 500);
    },

    handleLogout() {
      console.log('🚪 Iniciando logout...');
      
      // Fazer logout no authService
      authService.logout();
      
      // Forçar atualização da interface
      this.forceAuthUpdate();
      
      // Limpar dados da sessão
      this.wsConnected = false;
      
      // Redirecionar para login com garantia
      console.log('🔄 Redirecionando para login...');
      
      // Usar replace para evitar voltar com o botão voltar do navegador
      this.$router.replace('/login').then(() => {
        console.log('✅ Redirecionamento para login concluído');
        
        // Forçar limpeza completa da interface
        this.$nextTick(() => {
          // Limpar snackbar
          this.snackbar.show = false;
          
          // Forçar re-renderização
          if (this.$forceUpdate) {
            this.$forceUpdate();
          }
        });
      }).catch(error => {
        console.error('❌ Erro no redirecionamento:', error);
        
        // Fallback: tentar redirecionamento direto
        window.location.href = '/login';
      });
    },

    /**
     * Handler para mudanças no localStorage (ex: logout em outra aba)
     */
    handleStorageChange() {
      // Força a atualização da propriedade reativa de autenticação
      this.authCheckTrigger++;
    },

    /**
     * Handler para o evento personalizado de mudança de autenticação
     */
    handleAuthStateChanged() {
      this.authCheckTrigger++;
    },

    /**
     * Força atualização do estado de autenticação
     */
    forceAuthUpdate() {
      this.authCheckTrigger++;
    },
    
    /**
     * Força atualização da interface para corrigir problemas de layout
     */
    forceInterfaceUpdate() {
      console.log('🔄 Forçando atualização da interface...');
      
      // Forçar re-renderização do componente raiz
      if (this.$forceUpdate) {
        this.$forceUpdate();
      }
      
      // Forçar re-renderização de todos os componentes filhos
      this.$children.forEach(child => {
        if (child.$forceUpdate) {
          child.$forceUpdate();
        }
      });
      
      // Aguardar um tick e forçar novamente para garantir
      this.$nextTick(() => {
        if (this.$forceUpdate) {
          this.$forceUpdate();
        }
        
        // Notificar mudança de tema para forçar recálculo de layout
        if (this.$vuetify && this.$vuetify.theme) {
          const currentTheme = this.$vuetify.theme.dark;
          this.$vuetify.theme.dark = !currentTheme;
          this.$nextTick(() => {
            this.$vuetify.theme.dark = currentTheme;
          });
        }
      });
    },

    /**
     * Alterna entre tema claro e escuro
     */
    toggleTheme() {
      const newTheme = themeService.toggleTheme();
      
      // Aplicar tema no Vuetify de forma mais robusta
      if (this.$vuetify && this.$vuetify.theme) {
        this.$vuetify.theme.dark = newTheme === 'dark';
        
        // Forçar atualização do tema no Vuetify
        this.$nextTick(() => {
          this.$vuetify.theme.dark = newTheme === 'dark';
          
          // Forçar re-renderização de todos os componentes
          this.$forceUpdate();
          
          // Notificar todos os componentes filhos sobre a mudança
          this.$children.forEach(child => {
            if (child.$forceUpdate) {
              child.$forceUpdate();
            }
          });
        });
      }
      
      this.showNotification(
        `Tema alterado para ${newTheme === 'dark' ? 'escuro' : 'claro'}`, 
        'info'
      );
    },
    
    /**
     * Obter cor do chip baseado na role
     */
    getUserChipColor() {
      const role = this.currentUser?.role;
      if (role === 'admin') return 'orange';
      if (role === 'gestor') return 'purple';
      return 'blue';
    },
    
    /**
     * Obter ícone do chip baseado na role
     */
    getUserChipIcon() {
      const role = this.currentUser?.role;
      if (role === 'admin') return 'mdi-shield-crown';
      if (role === 'gestor') return 'mdi-account-tie';
      return 'mdi-account';
    },
    
    /**
     * Traduzir role para português
     */
    translateRole(role) {
      const translations = {
        'admin': 'Admin',
        'gestor': 'Gestor',
        'user': 'Usuário'
      };
      return translations[role] || role;
    }
  }
}
</script>

<style>
#app {
  font-family: 'Albert Sans', 'Roboto', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: all 0.3s ease;
}

/* Aplicar Albert Sans globalmente, exceto para elementos de código */
*:not(code):not(pre):not(kbd):not(samp):not(tt):not(.log-content) {
  font-family: 'Albert Sans', 'Roboto', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
}

/* Sobrescrever fontes do Vuetify */
.v-application,
.v-application .text-h1,
.v-application .text-h2,
.v-application .text-h3,
.v-application .text-h4,
.v-application .text-h5,
.v-application .text-h6,
.v-application .text-subtitle-1,
.v-application .text-subtitle-2,
.v-application .text-body-1,
.v-application .text-body-2,
.v-application .text-caption,
.v-application .text-overline,
.v-application .v-btn,
.v-application .v-card,
.v-application .v-list,
.v-application .v-chip,
.v-application .v-toolbar,
.v-application .v-navigation-drawer,
.v-application .v-app-bar {
  font-family: 'Albert Sans', 'Roboto', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
}

/* Garantir que elementos de log usem fontes monospace */
.log-content,
pre.log-content,
.log-content pre,
code,
pre,
kbd,
samp,
tt {
  font-family: 'Fira Code', 'Courier New', Courier, monospace !important;
}

/* Transição suave para mudanças de tema */
* {
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

/* Forçar formato 24h para campos de horário */
input[type="time"] {
  text-align: center;
  direction: ltr;
}

/* Garantir que o seletor de horário use formato 24h */
input[type="time"]::-webkit-calendar-picker-indicator {
  filter: invert(0.5);
  cursor: pointer;
}

/* Estilo para campos de time no Vuetify */
.v-text-field input[type="time"] {
  font-family: 'Albert Sans', 'Roboto', sans-serif;
  font-size: 14px;
  letter-spacing: 0.5px;
}

/* Garantir formato 24h no input time - remover indicadores de AM/PM */
input[type="time"]::-webkit-datetime-edit-ampm-field {
  display: none;
}

/* Evitar transições em elementos que não devem ter */
.v-ripple__container,
.v-overlay__content,
.v-menu__content,
.v-dialog__content,
.v-tooltip__content {
  transition: none !important;
}

/* Estilos modernos para o header */
.modern-app-bar {
  backdrop-filter: blur(10px);
  background: linear-gradient(135deg, var(--v-primary-base, #1976D2) 0%, var(--v-primary-darken1, #1565C0) 100%) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modern-app-bar.v-app-bar--is-scrolled {
  background: rgba(25, 118, 210, 0.95) !important;
}

/* Botões de navegação modernos */
.nav-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px !important;
  margin: 0 4px !important;
}

.nav-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.nav-btn.v-btn--active {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

/* Toggle de tema */
.theme-toggle {
  transition: all 0.3s ease;
  border-radius: 50% !important;
}

.theme-toggle:hover {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.1) !important;
}

/* Chips modernos */
.user-chip, .connection-chip {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  transition: all 0.3s ease;
}

.user-chip:hover, .connection-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Botão de logout */
.logout-btn {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.logout-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Tema escuro */
.theme-dark {
  background: #121212;
  color: #ffffff;
}

.theme-dark .modern-app-bar {
  background: linear-gradient(135deg, #1E1E1E 0%, #2D2D2D 100%) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-dark .nav-btn.v-btn--active {
  background: rgba(255, 255, 255, 0.1) !important;
}

.theme-dark .user-chip, .theme-dark .connection-chip {
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

/* Tema claro */
.theme-light {
  background: #FAFAFA;
  color: #333333;
}

/* Animações suaves */
.v-application {
  transition: background-color 0.3s ease, color 0.3s ease;
}

.v-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Melhorias nos botões */
.v-btn {
  border-radius: 8px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: none !important;
  font-weight: 500 !important;
}

.v-btn:hover {
  transform: translateY(-1px);
}

/* Melhorias nos chips */
.v-chip {
  border-radius: 16px !important;
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}

/* Melhorias nas tabelas */
.v-data-table {
  border-radius: 12px !important;
  overflow: hidden;
}

.v-data-table .v-data-table__wrapper {
  border-radius: 12px;
}

/* Melhorias nos diálogos */
.v-dialog .v-card {
  border-radius: 16px !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3) !important;
}

/* Melhorias nos campos de entrada */
.v-text-field .v-input__control .v-input__slot {
  border-radius: 8px !important;
}

.v-select .v-input__control .v-input__slot {
  border-radius: 8px !important;
}

/* Scrollbar personalizada */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

/* Tema escuro - scrollbar */
.theme-dark ::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.theme-dark ::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
}

.theme-dark ::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Responsividade */
@media (max-width: 768px) {
  .modern-app-bar .v-toolbar__title {
    font-size: 1.1rem !important;
  }
  
  .nav-btn {
    min-width: auto !important;
    padding: 0 8px !important;
  }
  
  .nav-btn .v-btn__content {
    flex-direction: column;
  }
  
  .nav-btn .v-icon {
    margin-bottom: 2px !important;
  }
}
</style> 